import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';

import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import PusherService from 'shared/services/pusher-service';
import { getCurrentCourse } from 'redux/selectors/course';
import { getCurrentUser } from 'redux/selectors/users';
import {
  fetchLearnerFinalSummary,
  setShowLearnerTakeaway,
} from 'redux/actions/learner-final-summary';
import {
  getLearnerFinalSummaryState,
  isLearnerFinalSummaryGenerating,
  isLearnerFinalSummaryReady,
  hasLearnerFinalSummaryError,
} from 'redux/selectors/learner-final-summary';
import { RecommendedResource } from 'redux/schemas/app/learner-final-summary';

import TakeawayTakeover from './takeaway-takeover';
import TakeawayProfessional from './takeaway-professional';
import GeneratingState from './generating-state';
import ErrorState from './error-state';
import { LearnerTakeawayView } from './view-toggle';
import { ltColors } from './styles';

const LEARNER_FINAL_SUMMARY_UPDATED_EVENT = 'learner_final_summary_updated';

const overlayStyles = css`
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: ${ltColors.navy};

  .lt-overlay-close {
    position: absolute;
    top: 16px;
    right: 20px;
    border: none;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.12);
    color: ${ltColors.white};
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }
`;

/**
 * Container for the learner end-of-course AI takeaway.
 *
 * Fetches the report on mount, listens for backend status updates, and renders
 * the full-screen takeaway experience only when another route or action has
 * explicitly opened it.
 */
const LearnerTakeawayTakeover: React.FC = () => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const [view, setView] = useState<LearnerTakeawayView>('playful');

  const currentCourse = useSelector(getCurrentCourse);
  const currentUser = useSelector(getCurrentUser);
  const summary = useSelector(getLearnerFinalSummaryState);
  const isReady = useSelector(isLearnerFinalSummaryReady);
  const hasError = useSelector(hasLearnerFinalSummaryError);

  const catalogId = currentCourse?.catalogId;
  const courseId = currentCourse?.id;
  const { show, report, errorMessage } = summary;

  useEffect(() => {
    if (catalogId) dispatch(fetchLearnerFinalSummary({ catalogId }));
  }, [catalogId, dispatch]);

  useEffect(() => {
    if (!courseId || !catalogId) return undefined;
    const channel = PusherService.initializeCourseChannel(courseId);
    if (!channel) return undefined;
    const handleUpdate = () => { dispatch(fetchLearnerFinalSummary({ catalogId })); };
    channel.bind(LEARNER_FINAL_SUMMARY_UPDATED_EVENT, handleUpdate);
    return () => { channel.unbind(LEARNER_FINAL_SUMMARY_UPDATED_EVENT, handleUpdate); };
  }, [courseId, catalogId, dispatch]);

  const close = () => dispatch(setShowLearnerTakeaway(false));
  const retry = () => { if (catalogId) dispatch(fetchLearnerFinalSummary({ catalogId })); };

  const openResource = (resource: RecommendedResource) => {
    if (resource.lecturePageId && currentCourse?.catalogId) {
      $state.go('lecture-page', {
        catalogId: currentCourse.catalogId,
        id: resource.lecturePageId,
        lectureActivityId: resource.componentId ?? undefined,
      });
      close();
    }
  };

  if (!show) return null;

  if (isReady && report) {
    if (view === 'professional') {
      return (
        <TakeawayProfessional
          report={report}
          learnerFirstName={currentUser?.firstName ?? ''}
          courseTitle={currentCourse?.title ?? ''}
          onClose={close}
          onOpenResource={openResource}
          view={view}
          onSetView={setView}
        />
      );
    }
    return (
      <TakeawayTakeover
        report={report}
        learnerFirstName={currentUser?.firstName ?? ''}
        learnerName={currentUser?.fullName ?? ''}
        learnerInitials={currentUser?.initials ?? ''}
        learnerProfilePicture={currentUser?.profilePicture ?? ''}
        courseTitle={currentCourse?.title ?? ''}
        onClose={close}
        onOpenResource={openResource}
        view={view}
        onSetView={setView}
      />
    );
  }

  return (
    <div css={overlayStyles} role='dialog' aria-modal='true' aria-label='Your AI takeaway'>
      <button type='button' className='lt-overlay-close' onClick={close} aria-label='Close'>
        <i className='icon icon-close' aria-hidden='true' />
      </button>
      {hasError ? <ErrorState message={errorMessage} onRetry={retry} /> : <GeneratingState />}
    </div>
  );
};

export default LearnerTakeawayTakeover;
